import { PatientWithSummaryReportsIncident } from '@/jsonapi/PatientWithSummaryReports'
import { getFriendlyEventName } from '@/view-model/patient-event-constants/MemberPatientEvents'
import moment from 'moment-timezone'
import * as R from 'ramda'
import {
  ConvertIncidentsOptions,
  IncidentForSeries,
} from '@/components/patient-stats/graphs/timeline-chart-options/convert-incidents.types'

const INCIDENT_FONT_WIDTH = 6.02 // when font-size = 10 with Roboto Mono
const INCIDENT_BASIC_MAX_TEXT_LENGTH = 20

// function truncateText(options: { text: string; maxLength: number }) {
//   const { text, maxLength } = options
//   return text.length <= maxLength ? text : text.substr(0, maxLength - 1) + '…'
// }

// TODO refactor
function separateWithLineBreak(options: { text: string; maxLength: number }) {
  const { text, maxLength } = options
  const words = text.split(' ')
  const result = []
  let lengthCounts = 0
  let wordCounts = 0
  words.forEach((word, index) => {
    lengthCounts += word.length + wordCounts
    wordCounts += 1

    if (lengthCounts > maxLength && index === 0) {
      result.push(word)
      result.push('\n')
      lengthCounts = 0
      wordCounts = 0
    } else {
      if (lengthCounts > maxLength) {
        result.push('\n')
        lengthCounts = 0
        wordCounts = 0
      }
      result.push(word)
    }
  })
  return result
    .join(' ')
    .replace(/\n\s/g, '\n')
    .replace(/\n$/, '')
}

function addParamsToIncident(timezone): (incident: PatientWithSummaryReportsIncident) => IncidentForSeries {
  return (incident: PatientWithSummaryReportsIncident) => {
    const friendlyDescription =
      incident.type === 'note' ? incident.description : getFriendlyEventName(incident.description)
    const truncatedDescription = separateWithLineBreak({
      text: friendlyDescription,
      maxLength: INCIDENT_BASIC_MAX_TEXT_LENGTH,
    })
    const startOfDay = moment(incident.timestamp)
      .tz(timezone)
      .startOf('day')
      .valueOf()
    return {
      ...incident,
      friendlyDescription,
      startOfDay,
      truncatedDescription: truncatedDescription,
      index: 0,
    }
  }
}

function diffDaysBetween(a: IncidentForSeries, b: IncidentForSeries): number {
  return moment(a.startOfDay - 1).diff(moment(b.startOfDay), 'days')
}

function searchNewIndex(options: {
  incident: IncidentForSeries
  latestIncidentByIndex: { [key: string]: IncidentForSeries }
  oneDayWidth: number
  offsetDistance: number
}): number {
  const { incident, latestIncidentByIndex, oneDayWidth, offsetDistance } = options
  const incidents = Object.values(latestIncidentByIndex)
  if (!incidents.length) {
    return 0
  }

  const prevIncWithEnoughSpace = incidents.find(prevInc => {
    const comparisonTextLength = Math.min(INCIDENT_BASIC_MAX_TEXT_LENGTH, prevInc.friendlyDescription.length)
    const needDaysToShow = Math.ceil((comparisonTextLength * INCIDENT_FONT_WIDTH + offsetDistance) / oneDayWidth)
    const diff = diffDaysBetween(incident, prevInc)
    return needDaysToShow <= diff
  })
  return prevIncWithEnoughSpace ? prevIncWithEnoughSpace.index : incidents[incidents.length - 1].index + 1
}

function applyIndex(options: {
  incidents: IncidentForSeries[]
  oneDayWidth: number
  offsetDistance: number
}): IncidentForSeries[] {
  const { incidents, oneDayWidth, offsetDistance } = options

  const latestIncidentByIndex: { [key: string]: IncidentForSeries } = {}
  return incidents.map(incident => {
    const updateIncident = {
      ...incident,
      index: searchNewIndex({
        incident,
        latestIncidentByIndex,
        oneDayWidth,
        offsetDistance,
      }),
    }
    latestIncidentByIndex[updateIncident.index] = updateIncident
    return updateIncident
  })
}

export function replaceTruncatedDescription(options: {
  incidents: IncidentForSeries[]
  oneDayWidth: number
  startOfDays: number[]
  chartWidth: number
  grid: echarts.EChartOption.Grid
  offsetDistance: number
}): IncidentForSeries[] {
  const { oneDayWidth, incidents, chartWidth, grid, startOfDays, offsetDistance } = options

  // for right edge of chart
  const numOfDays = startOfDays.length
  const startOfDayWithIndex: { [key: string]: number } = startOfDays.reduce((acc, cur, index) => {
    acc[cur] = index + 1
    return acc
  }, {})
  const rightPadding = Math.floor((chartWidth - chartWidth * Number(String(grid.width).replace('%', '')) * 0.01) / 2)

  const incidentsByIndex = R.groupBy(inc => String(inc.index), incidents)
  return Object.values(incidentsByIndex)
    .map(_incidents => {
      return _incidents.map((incident, index) => {
        const nextIncident = _incidents[index + 1]
        const diffDaysToNextInc = nextIncident ? diffDaysBetween(nextIncident, incident) : numOfDays
        const diffDaysToChartEnd = numOfDays - startOfDayWithIndex[incident.startOfDay]
        const needSpacingToChartEdge = diffDaysToChartEnd * oneDayWidth - offsetDistance + rightPadding
        const needSpacingToNextInc = diffDaysToNextInc * oneDayWidth - offsetDistance
        return {
          ...incident,
          truncatedDescription: separateWithLineBreak({
            text: incident.friendlyDescription,
            maxLength: Math.floor((nextIncident ? needSpacingToNextInc : needSpacingToChartEdge) / INCIDENT_FONT_WIDTH),
          }),
        }
      })
    })
    .flat()
}

export function convertIncidents(options: ConvertIncidentsOptions): IncidentForSeries[] {
  const {
    incidents,
    chartWidth,
    startOfDayTimestamps,
    timezone,
    grid,
    incidentLabelDistance,
    incidentLabelPadding,
    incidentSymbolSize,
  } = options

  const chartContentWidth = chartWidth * Number(String(grid.width).replace('%', '')) * 0.01
  const oneDayWidth = chartContentWidth / startOfDayTimestamps.length
  const offsetDistance = (incidentSymbolSize - oneDayWidth) / 2 + incidentLabelDistance + incidentLabelPadding * 2 // circle - square / 2(half of square) + distance + textarea padding LR

  const sortedUniqIncidents = R.uniqBy(incident => incident.timestamp, incidents).sort(
    (a, b) => a.timestamp - b.timestamp
  )
  const addedParamsIncidents = sortedUniqIncidents.map(addParamsToIncident(timezone))
  const incidentsAppliedIndex = applyIndex({
    incidents: addedParamsIncidents,
    oneDayWidth,
    offsetDistance,
  })
  const incidentsAppliedTruncatedDescription = replaceTruncatedDescription({
    incidents: incidentsAppliedIndex,
    oneDayWidth,
    chartWidth,
    startOfDays: startOfDayTimestamps,
    grid,
    offsetDistance,
  }).sort((a, b) => a.timestamp - b.timestamp)

  return incidentsAppliedTruncatedDescription
}
